import { useState, useMemo } from "react";
import { mockAccountingEntries } from "@/mocks/dealerData";

export default function AccountingPage() {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<"all" | "gelir" | "gider">("all");

  const filtered = useMemo(() => {
    let data = mockAccountingEntries;
    if (filterType !== "all") data = data.filter((e) => e.type === filterType);
    if (search) {
      const q = search.toLowerCase();
      data = data.filter((e) => e.description.toLowerCase().includes(q) || e.category.toLowerCase().includes(q));
    }
    return data;
  }, [filterType, search]);

  const totalIncome = filtered.filter((e) => e.type === "gelir").reduce((sum, e) => sum + e.amount, 0);
  const totalExpense = filtered.filter((e) => e.type === "gider").reduce((sum, e) => sum + e.amount, 0);
  const balance = totalIncome - totalExpense;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="accounting-title">Muhasebe</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Gelir ve gider kayıtlarınızı takip edin</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-48">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kayıt ara..." className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <button className="flex items-center gap-1.5 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i> Kayıt Ekle
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs text-foreground-400 mb-1">Toplam Gelir</p>
          <p className="text-xl font-bold text-primary-600">${totalIncome.toLocaleString("en-US")}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs text-foreground-400 mb-1">Toplam Gider</p>
          <p className="text-xl font-bold text-accent-600">${totalExpense.toLocaleString("en-US")}</p>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
          <p className="text-xs text-foreground-400 mb-1">Net Bakiye</p>
          <p className={`text-xl font-bold ${balance >= 0 ? "text-primary-600" : "text-accent-500"}`}>
            ${balance.toLocaleString("en-US")}
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4">
        {[{ key: "all", label: "Tümü" }, { key: "gelir", label: "Gelir" }, { key: "gider", label: "Gider" }].map((f) => (
          <button key={f.key} onClick={() => setFilterType(f.key as "all" | "gelir" | "gider")} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterType === f.key ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500 hover:text-foreground-700"}`}>{f.label}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {/* Desktop Table */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm min-w-[550px]">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tarih</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tür</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Kategori</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Açıklama</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Tutar</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <tr key={entry.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                  <td className="px-4 py-3 text-foreground-600 text-xs">{new Date(entry.date).toLocaleDateString("tr-TR")}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${entry.type === "gelir" ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"}`}>
                      <i className={entry.type === "gelir" ? "ri-arrow-up-line" : "ri-arrow-down-line"}></i>
                      {entry.type === "gelir" ? "Gelir" : "Gider"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-foreground-600">{entry.category}</td>
                  <td className="px-4 py-3 text-foreground-900">{entry.description}</td>
                  <td className={`px-4 py-3 text-right font-medium ${entry.type === "gelir" ? "text-primary-600" : "text-accent-500"}`}>
                    {entry.type === "gider" ? "-" : "+"}${entry.amount.toLocaleString("en-US")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-background-200/70">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-foreground-400">
              <i className="ri-calculator-line text-3xl block mb-2"></i>
              <p className="text-sm">Kayıt bulunamadı</p>
            </div>
          ) : (
            filtered.map((entry) => (
              <div key={entry.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${entry.type === "gelir" ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"}`}>
                        <i className={entry.type === "gelir" ? "ri-arrow-up-line" : "ri-arrow-down-line"}></i>
                        {entry.type === "gelir" ? "Gelir" : "Gider"}
                      </span>
                      <span className="text-xs text-foreground-400">{new Date(entry.date).toLocaleDateString("tr-TR")}</span>
                    </div>
                    <p className="text-sm font-medium text-foreground-900 mt-1">{entry.description}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{entry.category}</p>
                  </div>
                  <p className={`text-sm font-bold shrink-0 ${entry.type === "gelir" ? "text-primary-600" : "text-accent-500"}`}>
                    {entry.type === "gider" ? "-" : "+"}${entry.amount.toLocaleString("en-US")}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        {filtered.length === 0 && (
          <div className="hidden md:block text-center py-12 text-foreground-400">
            <i className="ri-calculator-line text-3xl block mb-2"></i>
            <p className="text-sm">Kayıt bulunamadı</p>
          </div>
        )}
      </div>
    </div>
  );
}